import * as React from 'react';

import { AlertTriangle } from 'lucide-react';
import { decodeJwtPayload } from '@klareospace/pharos-lib-utils';
import type { TemplateFormFieldDef, TemplateFormFieldOption } from '@klareospace/pharos-lib-types';
import type { Control, FieldValues } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui';
import { CredentialInput } from '../credential-input';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DynamicFormFieldProps {
  control: Control<FieldValues>;
  /**
   * Resolved options for `dynamic-select` fields, keyed by `optionsSource`.
   * The parent fetches these at install time and injects them here — this
   * component stays pure and never fetches data itself.
   *
   * Example:
   *   { 'sites': [{ label: 'Main Warehouse', value: 'wh-1' }] }
   *
   * When the map entry for a given `optionsSource` is absent or empty the
   * select renders disabled so the user cannot submit an invalid value.
   */
  dynamicOptions?: Record<string, TemplateFormFieldOption[]>;
  fieldDef: TemplateFormFieldDef;
  /**
   * Localised labels for the credential reveal toggle (password / jwt fields).
   * Injected by the parent — avoids a useTranslation dependency inside this lib.
   */
  revealLabels?: {
    hide: string;
    show: string;
  };
  /**
   * Localised label shown below a dynamic-select when its options list is empty.
   * Injected by the parent. English default provided.
   */
  selectEmptyLabel?: string;
  /**
   * Localised label shown below a dynamic-select while options are loading.
   * Injected by the parent. English default provided.
   * When absent, `selectEmptyLabel` is used as fallback.
   */
  selectLoadingLabel?: string;
}

// ─── JWT expiry helpers ───────────────────────────────────────────────────────

/** Number of milliseconds in one day. */
const MS_PER_DAY = 86_400_000;

/** Days within which an expiring JWT triggers a warning. */
const JWT_WARN_DAYS = 30;

interface JwtExpiryInfo {
  daysUntilExpiry: number | null;
  isExpired: boolean;
  isSoonExpiring: boolean;
}

/**
 * Decodes the `exp` claim from a JWT token (without verifying the signature)
 * and returns UI-level expiry information. Returns null/false fields when the
 * token is blank, malformed, or has no `exp` claim.
 *
 * Low-level base64url decode is delegated to decodeJwtPayload from pharos-lib-utils.
 */
function resolveJwtExpiryInfo(token: string): JwtExpiryInfo {
  const empty: JwtExpiryInfo = { daysUntilExpiry: null, isExpired: false, isSoonExpiring: false };
  if (!token || !token.trim()) {
    return empty;
  }
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload['exp'] !== 'number') {
    return empty;
  }
  const diffMs = payload['exp'] * 1000 - Date.now();
  const daysUntilExpiry = Math.floor(diffMs / MS_PER_DAY);
  return {
    daysUntilExpiry,
    isExpired: daysUntilExpiry < 0,
    isSoonExpiring: daysUntilExpiry >= 0 && daysUntilExpiry < JWT_WARN_DAYS,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface JwtExpiryBannerProps {
  daysUntilExpiry: number | null;
  isExpired: boolean;
  isSoonExpiring: boolean;
  labels: {
    expired: string;
    expiresInDays: string;
  };
}

function JwtExpiryBanner({
  daysUntilExpiry,
  isExpired,
  isSoonExpiring,
  labels,
}: JwtExpiryBannerProps): React.JSX.Element | null {
  if (!isExpired && !isSoonExpiring) {
    return null;
  }
  const message = isExpired ? labels.expired : labels.expiresInDays.replace('{{days}}', String(daysUntilExpiry));

  return (
    <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveInputType(fieldType: TemplateFormFieldDef['type']): string {
  switch (fieldType) {
    case 'number':
      return 'number';
    case 'url':
      return 'url';
    default:
      return 'text';
  }
}

// ─── JWT field renderer ───────────────────────────────────────────────────────

interface JwtFormFieldProps {
  control: Control<FieldValues>;
  fieldDef: TemplateFormFieldDef;
  /** Localised labels injected by the parent — avoids a useTranslation dependency inside this lib. */
  jwtLabels?: {
    expired: string;
    expiresInDays: string;
  };
  revealLabels?: {
    hide: string;
    show: string;
  };
}

function JwtFormField({
  control,
  fieldDef,
  jwtLabels = {
    expired: 'JWT has expired.',
    expiresInDays: 'JWT expires in {{days}} day(s).',
  },
  revealLabels = { hide: 'Hide', show: 'Show' },
}: JwtFormFieldProps): React.JSX.Element {
  const { key, label, required } = fieldDef;

  return (
    <FormField
      control={control}
      name={key}
      render={({ field }): React.JSX.Element => {
        const currentValue = (field.value as string) ?? '';
        const expiry = resolveJwtExpiryInfo(currentValue);
        return (
          <FormItem>
            <FormLabel>
              {label}
              {required ? <span className="text-destructive"> *</span> : null}
            </FormLabel>
            <FormControl>
              <CredentialInput
                {...field}
                hideLabel={revealLabels.hide}
                showLabel={revealLabels.show}
                value={currentValue}
              />
            </FormControl>
            <JwtExpiryBanner
              daysUntilExpiry={expiry.daysUntilExpiry}
              isExpired={expiry.isExpired}
              isSoonExpiring={expiry.isSoonExpiring}
              labels={jwtLabels}
            />
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

// ─── Password field renderer ──────────────────────────────────────────────────

interface PasswordFormFieldProps {
  control: Control<FieldValues>;
  fieldDef: TemplateFormFieldDef;
  revealLabels?: {
    hide: string;
    show: string;
  };
}

function PasswordFormField({
  control,
  fieldDef,
  revealLabels = { hide: 'Hide', show: 'Show' },
}: PasswordFormFieldProps): React.JSX.Element {
  const { key, label, required } = fieldDef;

  return (
    <FormField
      control={control}
      name={key}
      render={({ field }): React.JSX.Element => (
        <FormItem>
          <FormLabel>
            {label}
            {required ? <span className="text-destructive"> *</span> : null}
          </FormLabel>
          <FormControl>
            <CredentialInput
              {...field}
              hideLabel={revealLabels.hide}
              showLabel={revealLabels.show}
              value={(field.value as string) ?? ''}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// ─── Select renderer (shared by static "select" and "dynamic-select") ────────

interface SelectFormFieldProps {
  control: Control<FieldValues>;
  emptyHint?: string;
  fieldKey: string;
  isDisabled: boolean;
  label: string;
  options: TemplateFormFieldOption[];
  required: boolean;
}

function SelectFormField({
  control,
  emptyHint,
  fieldKey,
  isDisabled,
  label,
  options,
  required,
}: SelectFormFieldProps): React.JSX.Element {
  return (
    <FormField
      control={control}
      name={fieldKey}
      render={({ field }): React.JSX.Element => (
        <FormItem>
          <FormLabel>
            {label}
            {required ? <span className="text-destructive"> *</span> : null}
          </FormLabel>
          <Select disabled={isDisabled} onValueChange={field.onChange} value={(field.value as string) ?? ''}>
            <FormControl>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isDisabled && emptyHint ? (
            <FormDescription className="text-xs text-muted-foreground">{emptyHint}</FormDescription>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// ─── Dynamic-select renderer ──────────────────────────────────────────────────

interface DynamicSelectFormFieldProps {
  control: Control<FieldValues>;
  dynamicOptions: Record<string, TemplateFormFieldOption[]> | undefined;
  fieldDef: TemplateFormFieldDef;
  selectEmptyLabel: string;
  selectLoadingLabel: string;
}

function DynamicSelectFormField({
  control,
  dynamicOptions,
  fieldDef,
  selectEmptyLabel,
  selectLoadingLabel,
}: DynamicSelectFormFieldProps): React.JSX.Element {
  const { key, label, optionsSource, required } = fieldDef;
  const resolvedOptions = optionsSource !== undefined ? (dynamicOptions?.[optionsSource] ?? []) : [];
  const isDisabled = resolvedOptions.length === 0;
  const isLoading = optionsSource !== undefined && dynamicOptions?.[optionsSource] === undefined;
  const emptyHint = isLoading ? selectLoadingLabel : selectEmptyLabel;
  return (
    <SelectFormField
      control={control}
      emptyHint={emptyHint}
      fieldKey={key}
      isDisabled={isDisabled}
      label={label}
      options={resolvedOptions}
      required={required}
    />
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DynamicFormField({
  control,
  dynamicOptions,
  fieldDef,
  revealLabels,
  selectEmptyLabel = 'No options available',
  selectLoadingLabel = 'Loading options…',
}: DynamicFormFieldProps): React.JSX.Element {
  const { key, label, options, required, type } = fieldDef;

  if (type === 'jwt') {
    return <JwtFormField control={control} fieldDef={fieldDef} revealLabels={revealLabels} />;
  }

  if (type === 'password') {
    return <PasswordFormField control={control} fieldDef={fieldDef} revealLabels={revealLabels} />;
  }

  if (type === 'select' && options && options.length > 0) {
    return (
      <SelectFormField
        control={control}
        fieldKey={key}
        isDisabled={false}
        label={label}
        options={options}
        required={required}
      />
    );
  }

  if (type === 'dynamic-select') {
    return (
      <DynamicSelectFormField
        control={control}
        dynamicOptions={dynamicOptions}
        fieldDef={fieldDef}
        selectEmptyLabel={selectEmptyLabel}
        selectLoadingLabel={selectLoadingLabel}
      />
    );
  }

  const inputType = resolveInputType(type);

  return (
    <FormField
      control={control}
      name={key}
      render={({ field }): React.JSX.Element => (
        <FormItem>
          <FormLabel>
            {label}
            {required ? <span className="text-destructive"> *</span> : null}
          </FormLabel>
          <FormControl>
            <Input type={inputType} {...field} value={(field.value as string | number | undefined) ?? ''} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
